<script setup lang="ts">
import type { DropeeMappedTaskItem } from '@/types/task'
import TheBottomSheet from '@/components/TheBottomSheet.vue'
import TaskModalPrizeBox from '@/components/TaskModalPrizeBox.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import { computed, ref, watch } from 'vue'
import api from '@/services/api'
import { usePlayer } from '@/composables/game/player'
import { useConfig } from '@/composables/game/config'
import router from '@/router'
import { RouteName } from '@/routes'
import { T } from '@tolgee/vue'
import { useMysteryTask } from '@/composables/game/tasks'
import { formatDuration } from '@/helpers/date'

const props = defineProps<{
  show: boolean
  isClaiming: boolean
  task: DropeeMappedTaskItem | null
}>()
const emit = defineEmits<{
  (event: 'hide'): void,
  (event: 'claim'): void,
}>()

const { updatePlayer } = usePlayer()
const { updateConfig } = useConfig()
const { getCountDownSeconds } = useMysteryTask()

const taskCountDownSeconds = computed(() => {
  if (!props.task?.startsOn) return 0
  return getCountDownSeconds(props.task.startsOn)
})

const formattedCountDown = computed(() => formatDuration(taskCountDownSeconds.value))
const isAvailable = computed(() => taskCountDownSeconds.value > 0)

const actionCompletedAt = ref(props.task?.actionCompletedAt)
const isLoading = ref(false)

async function claim() {
  if (!props.task || props.task.isDone || !isAvailable.value) {
    emit('hide')
    return
  }

  if (!actionCompletedAt.value) {
    isLoading.value = true
    try {
      const { config, playerStats } = await api.doTaskAction(props.task.id as string)
      const taskFromResponse = config.tasks.find(task => task.id === props.task?.id)
      updateConfig(config)
      updatePlayer(playerStats)
      actionCompletedAt.value = taskFromResponse?.actionCompletedAt
    } finally {
      isLoading.value = false
      router.push({ name: RouteName.HOME })
    }
  } else {
    emit('claim')
  }
}

watch(() => props.task, task => {
  if (!task) return
  actionCompletedAt.value = task.actionCompletedAt
})
</script>

<template>
  <TheBottomSheet
    :show="show"
    @hide="$emit('hide')"
  >
    <div
      v-if="task"
      class="flex flex-col gap-y-6 items-center"
    >
      <img
        class="size-[120px] object-cover"
        :src="task.imageUrl"
      >
      <div class="flex flex-col items-center">
        <div class="text-light-white text-[24px] leading-8 font-bold -tracking-[0.48px]">
          {{ task.title }}
        </div>
        <div
          v-if="task.description"
          class="text-white/60 text-base font-semibold text-center"
        >
          {{ task.description }}
        </div>
        <div
          class="mt-3 flex items-center justify-center px-8 py-[14px] bg-dark-most-bottom-layer rounded-full"
        >
          <div class="text-[24px] leading-5 font-medium -tracking-[0.48px]">
            {{ formattedCountDown }}
          </div>
        </div>
      </div>
      <TaskModalPrizeBox
        v-if="task.reward"
        :reward="task.reward"
        :spins="task.spins"
      />
      <BaseButton
        class="mt-2 w-full"
        :disabled="task.isDone"
        :loading="isClaiming || isLoading"
        @click="claim"
      >
        <T
          v-if="!actionCompletedAt"
          key-name="quests.mystery_task.button_label"
          default-value="Let's go"
        />
        <T
          v-else
          key-name="quests.mystery_task.check"
          default-value="Check"
        />
      </BaseButton>
    </div>
  </TheBottomSheet>
</template>

<style scoped>

</style>